import { toast } from 'react-toastify';
import { all, call, fork, put, takeEvery, takeLatest } from 'redux-saga/effects';
import { ApiResponse } from 'api/ApiClient';
import { SuccessToastContainer } from 'components/Toast/ToastContainer';
import { GENERIC_TOAST_ERROR_MESSAGE, HTTPStatusCode } from 'config/const';
import { IJobTitleState } from 'store/types';
import { IAction, IApiList, IGetLaborResponse, ILabor, SiteJobSource } from 'types';
import { formatErrorMessages } from 'utils/errors';
import * as API from '../../api';
import * as Actions from './actions';
import { IGetLaborPayload, IGetPosJobTitlesPayload, IRestoreLaborPayload, IUpdateLaborPayload, LaborReview } from './types';

export function* getLabor({ payload }: IAction<LaborReview, IGetLaborPayload>) {
  try {
    const { siteId, date } = payload;
    const response: ApiResponse<IGetLaborResponse> = yield call(API.LaborReview.getLabor, siteId, date);

    yield put(Actions.getLaborSuccess(response.body));
  } catch (error: any) {
    yield put(Actions.getLaborError(error));
  }
}

// Kept in this screen's own state rather than the site's shared job titles cache (collections.jobs), which the rest
// of the app expects to hold every source: a shift's job title has to be one Legion knows, so only POS ones are offered.
export function* getPosJobTitles({ payload }: IAction<LaborReview, IGetPosJobTitlesPayload>) {
  try {
    const response: ApiResponse<IApiList<IJobTitleState>> = yield call(API.Sites.listJobs, payload.siteId, [SiteJobSource.POS]);

    yield put(Actions.getPosJobTitlesSuccess(response.body.data));
  } catch (error: any) {
    yield put(Actions.getPosJobTitlesError(error));
  }
}

export function* updateLabor({ payload }: IAction<LaborReview, IUpdateLaborPayload>) {
  try {
    const { siteId, date, locationId, employeeId, body, onSuccess } = payload;
    const response: ApiResponse<ILabor> = yield call(API.LaborReview.updateLabor, siteId, date, locationId, employeeId, body);

    // Stamped with the record it was saved for, which the reducer matches on: a response naming its location or employee
    // differently (or not at all) would otherwise add a second record instead of replacing this one.
    yield put(Actions.updateLaborSuccess({ ...response.body, locationId, employeeId }));

    if (onSuccess) yield call(onSuccess);

    yield call(toast, SuccessToastContainer({ message: 'Shift updated' }));
  } catch (error: any) {
    const formattedError = formatErrorMessages({ errors: error.body?.errors, statusCode: error.statusCode });
    if (payload.onError) yield call(payload.onError, formattedError);
    yield put(Actions.updateLaborError(formattedError, { message: getLaborFailToastMessage(error) }));
  }
}

export function* restoreLabor({ payload }: IAction<LaborReview, IRestoreLaborPayload>) {
  try {
    const { siteId, date, locationId, employeeId, body, onSuccess } = payload;
    const response: ApiResponse<ILabor> = yield call(API.LaborReview.restoreLabor, siteId, date, locationId, employeeId, body);

    // Stamped with the record it was saved for, which the reducer matches on: a response naming its location or employee
    // differently (or not at all) would otherwise add a second record instead of replacing this one.
    yield put(Actions.restoreLaborSuccess({ ...response.body, locationId, employeeId }));

    if (onSuccess) yield call(onSuccess);

    yield call(toast, SuccessToastContainer({ message: 'Shift restored' }));
  } catch (error: any) {
    const formattedError = formatErrorMessages({ errors: error.body?.errors, statusCode: error.statusCode });
    if (payload.onError) yield call(payload.onError, formattedError);
    yield put(Actions.restoreLaborError(formattedError, { message: getLaborFailToastMessage(error) }));
  }
}

// takeLatest: switching business days quickly must not let an earlier day's slower response land last.
export function* watchGetLabor() {
  yield takeLatest(LaborReview.GET_LABOR_REQUEST, getLabor);
}
export function* watchGetPosJobTitles() {
  yield takeLatest(LaborReview.GET_POS_JOB_TITLES_REQUEST, getPosJobTitles);
}
export function* watchUpdateLabor() {
  yield takeEvery(LaborReview.UPDATE_LABOR_REQUEST, updateLabor);
}
export function* watchRestoreLabor() {
  yield takeEvery(LaborReview.RESTORE_LABOR_REQUEST, restoreLabor);
}

function* laborReviewSaga() {
  yield all([fork(watchGetLabor), fork(watchGetPosJobTitles), fork(watchUpdateLabor), fork(watchRestoreLabor)]);
}
export { laborReviewSaga };

// The frontend applies the same validations as the API, so a 400 here is unexpected; a 404 means the
// record disappeared underneath the user. Neither has a message worth surfacing verbatim, so both
// collapse into the generic toast. A 409 means someone else saved a change first (optimistic locking
// on `version`), which the user can actually act on by refreshing.
export const LABOR_CONFLICT_ERROR_MESSAGE = 'This shift was already updated by someone else. Please refresh the page to see the latest data.';
const getLaborFailToastMessage = (error: any) => (error.statusCode === HTTPStatusCode.CONFLICT ? LABOR_CONFLICT_ERROR_MESSAGE : GENERIC_TOAST_ERROR_MESSAGE);
